import React, {useState, useEffect, lazy, Suspense} from 'react'
import {BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams} from 'react-router-dom'
import {motion, AnimatePresence} from 'framer-motion'
import {HelmetProvider} from 'react-helmet-async'
import {AuthProvider, useAuth} from './contexts/AuthContext'
import {ThemeProvider} from './contexts/ThemeContext'
import {AppearanceProvider} from './contexts/AppearanceContext'
import {ToastProvider} from './contexts/ToastContext'
import {NotificationProvider} from './contexts/NotificationContext'
import {AppConfigProvider} from './contexts/AppConfigContext'
import {ReviewPromptProvider} from './contexts/ReviewPromptContext'
import ReviewPromptModal from './components/ReviewPromptModal'
import MaintenanceGate from './components/MaintenanceGate'
import ForceUpdateGate from './components/ForceUpdateGate'
import {getUserGroups} from './services/groupService'
import {getRoundsWithInvitationStatus} from './services/roundService'
import { trackPageView } from './services/analytics'
import { App as CapacitorApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { isNative, isIOS } from './services/platformService'
import { useNativeInit } from './hooks/useNativeInit'
import {
  initPushNotifications,
  savePushTokenToServer,
  refreshPushToken,
  removePushListeners
} from './services/pushNotificationService'
import Header from './components/Header'
import BottomNavigation from './components/BottomNavigation'
import BackToTopButton from './components/BackToTopButton'
import ProtectedLayout from './components/ProtectedLayout'
import ErrorBoundary from './components/ErrorBoundary'
import Dashboard from './pages/Dashboard'
import Groups from './pages/Groups'
import Rounds from './pages/Rounds'
import Members from './pages/Members'
import Friends from './pages/Friends'
import Settings from './pages/Settings'
import Upgrade from './pages/Upgrade'
import MyAvailability from './pages/MyAvailability'
import CreateGroup from './pages/CreateGroup'
import CreateRound from './pages/CreateRound'
import CreateRoundWizard from './pages/CreateRoundWizard'
import SmartScheduleResults from './pages/SmartScheduleResults'
import SmartScheduleResultsSolo from './pages/SmartScheduleResultsSolo'
import GroupDetails from './pages/GroupDetails'
import RoundDetailPage from './pages/RoundDetailPage'
import JoinGroup from './pages/JoinGroup'
import Login from './pages/Login'
import SignUp from './pages/SignUp'
import EmailConfirmation from './pages/EmailConfirmation'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AuthCallback from './pages/AuthCallback'
import NotificationCenter from './pages/NotificationCenter'
import ScrollToTop from './components/ScrollToTop'
import AdminRoute from './components/AdminRoute'
import Logo from './components/common/Logo'
import { isMarketingHost, isAppHost } from './utils/host'

// Capacitor (iOS / Android) builds set VITE_BUILD_TARGET=native via the
// build:ios / build:android scripts in package.json. Native shells only
// ever see the in-app routes - never the marketing site - so we gate the
// lazy() declarations behind this flag. With a build-time constant, Rollup
// tree-shakes the dynamic imports + drops the marketing chunks (Landing,
// Features, HowItWorks, Pricing, Integrations, MarketingLayout) from the
// native bundle entirely.
const IS_NATIVE_BUILD = import.meta.env.VITE_BUILD_TARGET === 'native'

// Lazy-loaded pages (less frequently accessed, reduces initial bundle)
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const Welcome = lazy(() => import('./pages/Welcome'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'))
const Support = lazy(() => import('./pages/Support'))
const NotFound = lazy(() => import('./pages/NotFound'))
const OutlookCallback = lazy(() => import('./pages/OutlookCallback'))
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'))

// Marketing pages - excluded from native builds.
const Landing = IS_NATIVE_BUILD ? null : lazy(() => import('./pages/Landing'))
const Features = IS_NATIVE_BUILD ? null : lazy(() => import('./pages/Features'))
const HowItWorks = IS_NATIVE_BUILD ? null : lazy(() => import('./pages/HowItWorks'))
const Pricing = IS_NATIVE_BUILD ? null : lazy(() => import('./pages/Pricing'))
const Integrations = IS_NATIVE_BUILD ? null : lazy(() => import('./pages/Integrations'))
const MarketingLayout = IS_NATIVE_BUILD
  ? null
  : lazy(() => import('./components/marketing/MarketingLayout'))
// Public guest RSVP page (/r/:shortCode) - web builds only, same
// tree-shaking treatment as the marketing pages.
const GuestRoundPage = IS_NATIVE_BUILD ? null : lazy(() => import('./pages/GuestRoundPage'))
const HostGuard = IS_NATIVE_BUILD
  ? ({ children }) => children
  : lazy(() => import('./components/marketing/HostGuard'))

// Loading fallback for lazy-loaded routes. Generic skeleton shell - we don't
// know which route is loading at this point, so it's a few card placeholders
// rather than a route-specific layout. Per-page skeletons take over once the
// chunk lands and the page's own loading state kicks in.
const PageLoadingFallback = () => (
  <div className="min-h-screen bg-app">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="shimmer h-11 w-full rounded-lg" />
      <div className="space-y-4">
        <div className="shimmer h-32 w-full rounded-lg" />
        <div className="shimmer h-32 w-full rounded-lg" />
      </div>
    </div>
  </div>
)

// Component to track page views on route changes
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location]);

  return null; // This component doesn't render anything
}

// Hides the splash screen + adds native-* body classes regardless of which
// gate (force-update / maintenance) is rendering. Must mount above the gates
// so blocking screens can fade in instead of leaving the splash up forever.
function NativeInit() {
  useNativeInit()
  return null
}

// Routes that intentionally render flush to the top of the viewport on iOS
// (designed to extend under the status bar). Anything not in this list gets
// the standard `body.native-ios` padding (status bar + header) applied.
// Includes prefixes - `startsWith` semantics - so child routes inherit.
const AUTH_PAGE_PATHS = [
  '/',
  '/login',
  '/signup',
  '/welcome',
  '/email-confirmation',
  '/reset-password',
  '/forgot-password',
  '/onboarding',
  '/features',
  '/how-it-works',
  '/pricing',
  '/integrations',
  '/privacy',
  '/terms',
  '/knowledge',
  '/support',
]

// Legacy round-creation paths that took a :groupId redirect into the new
// wizard with the group pre-selected via the ?group= query param. Kept as
// small helper components so old links / bookmarks still land in the right
// place after the legacy routes were removed.
function LegacySmartGroupRedirect() {
  const { groupId } = useParams()
  return <Navigate to={`/create-round/smart/when?group=${groupId}`} replace />
}
function LegacyQuickGroupRedirect() {
  const { groupId } = useParams()
  return <Navigate to={`/create-round/quick/when?group=${groupId}`} replace />
}

function AppContent() {
  const {user, loading, onboardingCompleted, onboardingLoading} = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Defensive: the `auth-page` body class (set by Login / Onboarding / etc.
  // to extend content under the status bar) sometimes survives a route
  // change if the previous page's useEffect cleanup didn't fire - e.g.
  // when an AnimatePresence exit animation overlaps with the new route's
  // mount. The stuck class flips `body.native-ios.auth-page`'s
  // `padding-top: 0`, which then hides the header-width of every
  // subsequent app page (Settings, Rounds, etc.) behind the fixed header.
  //
  // On every route change, force the class state to match the route.
  useEffect(() => {
    const isAuthPath = AUTH_PAGE_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
    )
    if (isAuthPath) {
      document.documentElement.classList.add('auth-page')
      document.body.classList.add('auth-page')
    } else {
      document.documentElement.classList.remove('auth-page')
      document.body.classList.remove('auth-page')
    }
  }, [location.pathname])
  const [groups, setGroups] = useState([])
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [rounds, setRounds] = useState([])
  const [roundsLoading, setRoundsLoading] = useState(false)
  const [showJoinModal, setShowJoinModal] = useState(false)

  // Helper function to detect password reset flow from URL
  const isPasswordResetFlow = () => {
    // If we're on the reset-password page, allow access regardless of parameters
    // This handles cases where Supabase redirects to /reset-password after processing ConfirmationURL
    if (window.location.pathname === '/reset-password') {
      return true
    }
    
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    
    // Check for Supabase recovery tokens in URL params or hash
    const hasAccessToken = urlParams.has('access_token') || hashParams.has('access_token')
    const isRecoveryType = urlParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery'
    
    return hasAccessToken && isRecoveryType
  }

  // Load user's groups and rounds when authenticated
  useEffect(() => {
    console.log('📱 App useEffect - user changed:', user?.id, user?.email);
    if (user) {
      console.log('📱 User authenticated, loading groups and rounds...');

      loadGroups()
      loadRounds()
    } else {
      console.log('📱 No user, clearing groups and rounds');
      setGroups([])
      setRounds([])
    }
  }, [user])

  // Track previous user to detect logout
  const prevUserRef = React.useRef(null);

  // Initialise push notifications when user is authenticated on native platforms
  useEffect(() => {
    // Detect logout (had user before, now don't)
    const wasLoggedIn = prevUserRef.current !== null;
    const isLoggedOut = !user && wasLoggedIn;

    // Update ref for next render
    prevUserRef.current = user;

    // Clean up listeners on actual logout only
    if (isLoggedOut && isNative()) {
      console.log('🔔 User logged out, removing push listeners...');
      removePushListeners();
      return;
    }

    // Don't proceed if no user or not native
    if (!user || !isNative()) return;

    const setupPushNotifications = async () => {
      try {
        console.log('🔔 Initialising push notifications...');

        const result = await initPushNotifications({
          onRegistration: async (token) => {
            console.log('🔔 Push token received, saving to server...');
            await savePushTokenToServer(token);
          },
          onNotification: (notification) => {
            // Handle notification received while app is in foreground
            console.log('🔔 Notification received:', notification.title);
            // Could show an in-app notification banner here
          },
          onNotificationAction: (action) => {
            // Handle notification tap - navigate to relevant screen
            console.log('🔔 Notification tapped:', action);
            const data = action.notification?.data;

            // Explicit deep_link wins (admin broadcasts, future trigger payloads).
            // Accept either an in-app path or a teesync:// URL - strip the scheme.
            if (data?.deep_link) {
              const target = String(data.deep_link).replace(/^teesync:\/\//, '/');
              navigate(target.startsWith('/') ? target : `/${target}`);
            } else if (data?.type === 'round_invitation' && data?.roundId) {
              navigate(`/rounds`);
            } else if (data?.type === 'round_response' && data?.roundId) {
              navigate(`/rounds`);
            } else if (data?.type === 'round_open_invite_created' && data?.shortCode) {
              navigate(`/round/${data.shortCode}`);
            } else if (data?.type === 'availability_request') {
              navigate(`/my-availability`);
            }
          },
          onError: (error) => {
            console.error('🔔 Push notification error:', error);
          },
          // Request permission on first sign-in (guarded by user check above)
          requestPermissionImmediately: true,
        });

        console.log('🔔 Push notification init result:', result);

        // If already registered, refresh token to handle iOS token rotation
        if (result.registered) {
          await refreshPushToken();
        }
      } catch (error) {
        console.error('🔔 Error setting up push notifications:', error);
      }
    };

    setupPushNotifications();

    // No cleanup here - we only clean up on actual logout (handled above)
  }, [user, navigate])

  // Handle Universal Links and Custom URL Schemes for native app OAuth callbacks
  useEffect(() => {
    if (!isNative()) return;

    const handleAppUrlOpen = async (event) => {
      console.log('🔗 App URL opened:', event.url);

      try {
        const url = new URL(event.url);
        const pathname = url.pathname;
        const searchParams = url.searchParams;

        // Custom URL scheme uses format: teesync://auth/callback/outlook
        // Universal Link uses format: https://teesync.co.uk/auth/callback/outlook
        const isCustomScheme = url.protocol === 'teesync:';

        // Handle Outlook OAuth callback (both Universal Link and custom scheme)
        if (pathname === '/auth/callback/outlook') {
          console.log('🔗 Handling Outlook OAuth callback via', isCustomScheme ? 'custom scheme' : 'Universal Link');
          const code = searchParams.get('code');
          const state = searchParams.get('state');
          const error = searchParams.get('error');

          // Close the in-app browser
          try {
            await Browser.close();
          } catch {
            // Browser may already be closed
          }

          if (error) {
            console.error('🔗 Outlook OAuth error:', error);
            if (window._outlookOAuthReject) {
              window._outlookOAuthReject(new Error(error));
              delete window._outlookOAuthResolve;
              delete window._outlookOAuthReject;
            }
            return;
          }

          // Navigate to the callback page to complete the flow
          // The callback page will handle the code exchange
          if (code && state) {
            navigate(`/auth/callback/outlook?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&native=true`);
          }
        }

        // Handle Google OAuth callback (both Universal Link and custom scheme)
        if (pathname === '/auth/callback/google') {
          console.log('🔗 Handling Google OAuth callback via', isCustomScheme ? 'custom scheme' : 'Universal Link');
          const code = searchParams.get('code');
          const state = searchParams.get('state');
          const error = searchParams.get('error');

          // Close the in-app browser
          try {
            await Browser.close();
          } catch {
            // Browser may already be closed
          }

          if (error) {
            console.error('🔗 Google OAuth error:', error);
            if (window._googleOAuthReject) {
              window._googleOAuthReject(new Error(error));
              delete window._googleOAuthResolve;
              delete window._googleOAuthReject;
            }
            return;
          }

          // Navigate to the callback page to complete the flow
          if (code && state) {
            navigate(`/auth/callback/google?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&native=true`);
          }
        }
      } catch (err) {
        console.error('🔗 Error handling App URL:', err);
      }
    };

    // Register the URL listener
    const urlListener = CapacitorApp.addListener('appUrlOpen', handleAppUrlOpen);

    // Cleanup listener on unmount
    return () => {
      urlListener.then(listener => listener.remove());
    };
  }, [navigate])

  const loadGroups = async () => {
    console.log('📱 App.loadGroups() called');
    setGroupsLoading(true)
    try {
      console.log('📱 Calling getUserGroups()...');
      // Pass the current user to support test users (who aren't in Supabase Auth)
      const {data, error} = await getUserGroups(user)
      console.log('📱 getUserGroups() result:', { data: data?.length, error });
      if (error) {
        console.error('Error loading groups:', error)
        setGroups([])
      } else {
        console.log('📱 Setting groups:', data?.length, 'groups');
        setGroups(data || [])
      }
    } catch (err) {
      console.error('Exception loading groups:', err)
      setGroups([])
    } finally {
      setGroupsLoading(false)
    }
  }

  const handleJoinSuccess = async () => {
    await loadGroups()
    await loadRounds()
  }

  const handleRefresh = async () => {
    await Promise.all([loadGroups(), loadRounds()])
  }

  const loadRounds = async () => {
    setRoundsLoading(true)
    try {
      const {data, error} = await getRoundsWithInvitationStatus(null, user)
      if (error) {
        console.error('Error loading rounds:', error)
        setRounds([])
      } else {
        setRounds(data || [])
      }
    } catch (err) {
      console.error('Exception loading rounds:', err)
      setRounds([])
    } finally {
      setRoundsLoading(false)
    }
  }

  const handleGroupDeleted = async () => {
    await loadGroups()
    await loadRounds()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <motion.div
          initial={{opacity: 0, scale: 0.9}}
          animate={{opacity: 1, scale: 1}}
          className="text-center"
        >
          <motion.div
            animate={{rotate: 360}}
            transition={{duration: 2, repeat: Infinity, ease: "linear"}}
            className="w-16 h-16 bg-accent rounded-lg flex items-center justify-center mx-auto mb-6"
          >
            <Logo size="lg" variant="white" />
          </motion.div>
          <p className="text-text-secondary font-medium">Loading TeeSync...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-app">
      <AnimatePresence mode="wait">
        <PageViewTracker />
        <Suspense fallback={<PageLoadingFallback />}>
        <HostGuard>
        <Routes>
          {/* ========================================
              PUBLIC ROUTES - No authentication required
              ======================================== */}

          {/* Root route - three paths depending on host mode:
              - 'marketing' (teesync.co.uk): always render the marketing
                Landing inside MarketingLayout, regardless of auth state.
                Brochure pages don't care if you're logged in elsewhere.
              - 'app' (app.teesync.co.uk) + 'unified' (localhost / previews):
                fall through to today's auth-conditional logic - Dashboard
                for authed users (with onboarding redirect), iOS Welcome
                for unauth iOS, marketing Landing for unauth web.
              The host-mode check is runtime; same build artifact deploys
              to both Netlify sites. */}
          <Route
            path="/"
            element={
              !IS_NATIVE_BUILD && isMarketingHost() ? (
                <MarketingLayout>
                  <Landing />
                </MarketingLayout>
              ) : user ? (
                !onboardingLoading && !onboardingCompleted ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                <>
                  <Header
                    onJoinSuccess={handleJoinSuccess}
                    onSettingsClick={() => navigate('/settings')}
                  />
                  <main>
                    <motion.div
                      key="dashboard"
                      initial={{opacity: 0, y: 20}}
                      animate={{opacity: 1, y: 0}}
                      exit={{opacity: 0, y: -20}}
                      transition={{duration: 0.3}}
                    >
                      <Dashboard
                        groups={groups}
                        loading={groupsLoading}
                        onJoinSuccess={handleJoinSuccess}
                        onRefresh={handleRefresh}
                      />
                    </motion.div>
                  </main>
                  <BottomNavigation />
                  <BackToTopButton />
                </>
                )
              ) : isIOS() ? (
                <Welcome />
              ) : isAppHost() ? (
                // Unauthenticated web user on app.teesync.co.uk -
                // bounce them straight to the login form rather than
                // rendering the marketing Landing on the app subdomain.
                <Navigate to="/login" replace />
              ) : !IS_NATIVE_BUILD ? (
                <MarketingLayout>
                  <Landing />
                </MarketingLayout>
              ) : (
                <Welcome />
              )
            }
          />

          {/* Public marketing pages - share MarketingLayout (header + footer).
              Whole block omitted in native builds (no Landing / Features /
              HowItWorks / Pricing / Integrations chunks shipped to iOS). */}
          {!IS_NATIVE_BUILD && (
            <Route
              element={
                <MarketingLayout>
                  <Outlet />
                </MarketingLayout>
              }
            >
              <Route path="/features" element={<Features />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/integrations" element={<Integrations />} />
            </Route>
          )}

          {/* Guest round RSVP - public, no layout chrome. Authed users with
              an account still get the lightweight guest view here; the
              authed round detail remains at /round/:shortCode. */}
          {!IS_NATIVE_BUILD && (
            <Route path="/r/:shortCode" element={<GuestRoundPage />} />
          )}

          {/* Auth routes */}
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <Login />}
          />
          <Route
            path="/signup"
            element={!user ? <SignUp /> : <Navigate to="/" replace />}
          />
          <Route
            path="/email-confirmation"
            element={!user ? <EmailConfirmation /> : <Navigate to="/" replace />}
          />
          <Route
            path="/forgot-password"
            element={!user ? <ForgotPassword /> : <Navigate to="/" replace />}
          />
          <Route
            path="/reset-password"
            element={
              !user || isPasswordResetFlow() ? (
                <motion.div
                  key="reset-password"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <ResetPassword />
                </motion.div>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Auth callback routes */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/callback/outlook" element={<OutlookCallback />} />
          <Route path="/auth/callback/google" element={<GoogleCallback />} />

          {/* Public content pages */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/knowledge" element={<KnowledgeBase />} />
          <Route path="/support" element={<Support />} />

          {/* Admin route - has its own auth check via AdminRoute */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <motion.div
                  key="admin-dashboard"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <AdminDashboard />
                </motion.div>
              </AdminRoute>
            }
          />

          {/* ========================================
              PROTECTED ROUTES - Authentication required
              Uses ProtectedLayout for Header + nav
              ======================================== */}
          <Route element={<ProtectedLayout groups={groups} onJoinSuccess={handleJoinSuccess} />}>
            <Route
              path="/onboarding"
              element={
                <motion.div
                  key="onboarding"
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  exit={{opacity: 0}}
                  transition={{duration: 0.2}}
                >
                  <Onboarding />
                </motion.div>
              }
            />

            <Route
              path="/groups"
              element={
                <motion.div
                  key="groups"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <Groups
                    groups={groups}
                    loading={groupsLoading}
                    onJoinSuccess={handleJoinSuccess}
                  />
                </motion.div>
              }
            />

            <Route
              path="/rounds"
              element={
                <motion.div
                  key="rounds"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <Rounds groups={groups} />
                </motion.div>
              }
            />

            <Route
              path="/members"
              element={
                <motion.div
                  key="members"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <Members groups={groups} />
                </motion.div>
              }
            />

            <Route
              path="/friends"
              element={
                <motion.div
                  key="friends"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <Friends />
                </motion.div>
              }
            />

            {/* Legacy /schedule-friends removed — use the wizard at
                /create-round/quick/who?who=friends instead. */}
            <Route path="/schedule-friends" element={<Navigate to="/create-round/quick/who?who=friends" replace />} />

            {/* Redirect old /profile URL to /settings */}
            <Route path="/profile" element={<Navigate to="/settings" replace />} />

            <Route
              path="/settings"
              element={
                <motion.div
                  key="settings"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <Settings />
                </motion.div>
              }
            />

            <Route
              path="/upgrade"
              element={
                <motion.div
                  key="upgrade"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <Upgrade />
                </motion.div>
              }
            />

            <Route
              path="/my-availability"
              element={
                <motion.div
                  key="my-availability"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <MyAvailability />
                </motion.div>
              }
            />

            <Route
              path="/notifications"
              element={
                <motion.div
                  key="notifications"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <NotificationCenter />
                </motion.div>
              }
            />

            <Route
              path="/create-group"
              element={
                <motion.div
                  key="create-group"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <CreateGroup onAddGroup={handleJoinSuccess} />
                </motion.div>
              }
            />

            <Route
              path="/create-round/:groupId?"
              element={
                <motion.div
                  key="create-round"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <ErrorBoundary>
                    <CreateRound groups={groups} loading={groupsLoading} />
                  </ErrorBoundary>
                </motion.div>
              }
            />

            {/* New 3-screen wizard - replaces the WHO-first /create-round selector. */}
            <Route
              path="/create-round/:mode/:step"
              element={
                <motion.div
                  key="create-round-wizard"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <ErrorBoundary>
                    <CreateRoundWizard />
                  </ErrorBoundary>
                </motion.div>
              }
            />

            {/* Legacy setup routes redirect into the wizard. The wizard's
                URL query params (?group=<id>, ?who=friends|solo) hydrate
                the picker. /smart-schedule-results/* stays — the wizard
                navigates *into* the results pages on submit. */}
            <Route
              path="/create-round/smart-schedule/:groupId"
              element={<LegacySmartGroupRedirect />}
            />
            <Route path="/create-round/smart-schedule/solo" element={<Navigate to="/create-round/smart/when?who=solo" replace />} />
            <Route path="/create-round/quick-schedule/:groupId" element={<LegacyQuickGroupRedirect />} />
            <Route path="/create-round/quick-schedule/solo" element={<Navigate to="/create-round/quick/when?who=solo" replace />} />
            <Route path="/smart-schedule-friends" element={<Navigate to="/create-round/smart/when?who=friends" replace />} />

            <Route
              path="/smart-schedule-results/friends"
              element={
                <motion.div
                  key="smart-schedule-results-friends"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <ErrorBoundary>
                    <SmartScheduleResults />
                  </ErrorBoundary>
                </motion.div>
              }
            />

            <Route
              path="/smart-schedule-results/:groupId"
              element={
                <motion.div
                  key="smart-schedule-results"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <ErrorBoundary>
                    <SmartScheduleResults />
                  </ErrorBoundary>
                </motion.div>
              }
            />

            <Route
              path="/smart-schedule-results/solo"
              element={
                <motion.div
                  key="smart-schedule-results-solo"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <ErrorBoundary>
                    <SmartScheduleResultsSolo />
                  </ErrorBoundary>
                </motion.div>
              }
            />

            <Route
              path="/join-group"
              element={
                <motion.div
                  key="join-group-standalone"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <JoinGroup onJoinGroup={handleJoinSuccess} />
                </motion.div>
              }
            />

            <Route
              path="/round/:shortCode"
              element={
                <motion.div
                  key="round-detail"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <ErrorBoundary>
                    <RoundDetailPage groups={groups} />
                  </ErrorBoundary>
                </motion.div>
              }
            />

            <Route
              path="/group/:id"
              element={
                <motion.div
                  key="group-details"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -20}}
                  transition={{duration: 0.3}}
                >
                  <GroupDetails
                    groups={groups}
                    onGroupDeleted={handleGroupDeleted}
                  />
                </motion.div>
              }
            />
          </Route>

          {/* ========================================
              404 CATCH-ALL - Must be LAST route
              Shows 404 for any unmatched path
              ======================================== */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </HostGuard>
        </Suspense>
      </AnimatePresence>
    </div>
  )
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AuthProvider>
          <AppearanceProvider>
            <ThemeProvider>
              <NotificationProvider>
                <ToastProvider>
                  <AppConfigProvider>
                    <ReviewPromptProvider>
                      <NativeInit />
                      <ForceUpdateGate>
                        <MaintenanceGate>
                          <AppContent />
                        </MaintenanceGate>
                      </ForceUpdateGate>
                      <ReviewPromptModal />
                    </ReviewPromptProvider>
                  </AppConfigProvider>
                </ToastProvider>
              </NotificationProvider>
            </ThemeProvider>
          </AppearanceProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  )
}

export default App